import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { TextField, AreaField, SelectField, ConsentBox, Honeypot } from "@/components/form";
import { api, apiError } from "@/lib/api";

const EMPTY = {
  name: "", description: "", category: "", city: "", state: "", service_areas: [],
  website: "", public_phone: "", public_email: "", faith_statement: "", community_involvement: "",
  submitter_name: "", submitter_email: "",
  affirm_christian: false, affirm_accurate: false, affirm_standards: false, hp_field: "",
};

const AREA_LABELS = { local: "Local", online: "Online", nationwide: "Nationwide" };

export function BusinessSubmitDialog({ open, onOpenChange, categories }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);
  const toggleArea = (a) => setForm((f) => ({
    ...f, service_areas: f.service_areas.includes(a) ? f.service_areas.filter((x) => x !== a) : [...f.service_areas, a],
  }));

  const reset = () => { setForm(EMPTY); setDone(false); };
  const close = () => { onOpenChange(false); setTimeout(reset, 200); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) return toast.error("Please choose a category.");
    if (!form.affirm_christian || !form.affirm_accurate || !form.affirm_standards)
      return toast.error("Please confirm all three affirmations.");
    setSubmitting(true);
    try {
      await api.post("/businesses/submit", form);
      setDone(true);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="submit-business-dialog">
        {done ? (
          <div className="py-8 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-emerald" />
            <DialogTitle className="mt-4 font-display text-2xl text-navy">Submitted for review</DialogTitle>
            <p className="mt-3 text-muted-foreground">
              Thank you. Every listing goes into an approval queue before it is published. We'll review
              your submission and reach out if we need anything.
            </p>
            <Button className="mt-6 rounded-full bg-emerald hover:bg-emerald-dark text-white px-8" onClick={close}>Close</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display text-3xl text-navy">Submit Your Business</DialogTitle>
              <DialogDescription>
                Listings are free. Public listing details are separate from your personal application
                details. All submissions are reviewed before publication.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="grid gap-5 mt-2" data-testid="business-form">
              <Honeypot value={form.hp_field} onChange={set("hp_field")} />

              <p className="text-xs font-semibold uppercase tracking-wider text-gold-dark">Public listing</p>
              <TextField label="Business name" name="name" required value={form.name} onChange={onInput("name")} />
              <AreaField label="Description" name="description" required value={form.description} onChange={onInput("description")} />
              <SelectField label="Category" name="category" required value={form.category} onValueChange={set("category")}
                placeholder="Choose a category" options={categories} />
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField label="City" name="city" required value={form.city} onChange={onInput("city")} />
                <TextField label="State" name="state" required value={form.state} onChange={onInput("state")} />
              </div>
              <div>
                <Label className="text-sm font-medium text-navy">Service area</Label>
                <div className="mt-2 flex flex-wrap gap-4">
                  {Object.keys(AREA_LABELS).map((a) => (
                    <label key={a} className="flex items-center gap-2 cursor-pointer text-sm">
                      <Checkbox checked={form.service_areas.includes(a)} onCheckedChange={() => toggleArea(a)}
                        data-testid={`area-${a}`} className="data-[state=checked]:bg-emerald data-[state=checked]:border-emerald" />
                      {AREA_LABELS[a]}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <TextField label="Website (optional)" name="website" value={form.website} onChange={onInput("website")} placeholder="https://" />
                <TextField label="Public phone (optional)" name="public_phone" value={form.public_phone} onChange={onInput("public_phone")} />
              </div>
              <TextField label="Public email (optional)" name="public_email" type="email" value={form.public_email} onChange={onInput("public_email")} />
              <AreaField label="How your Christian faith informs your business" name="faith_statement" required
                value={form.faith_statement} onChange={onInput("faith_statement")}
                hint="This is your own statement and will be shown as owner-submitted." />
              <AreaField label="Community or ministry involvement (optional)" name="community_involvement"
                value={form.community_involvement} onChange={onInput("community_involvement")} />

              <div className="border-t border-border pt-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-gold-dark">Your application (kept private)</p>
                <div className="grid gap-5 sm:grid-cols-2 mt-4">
                  <TextField label="Your name" name="submitter_name" required value={form.submitter_name} onChange={onInput("submitter_name")} />
                  <TextField label="Your email" name="submitter_email" type="email" required value={form.submitter_email} onChange={onInput("submitter_email")} />
                </div>
              </div>

              <div className="rounded-lg bg-ivory-alt p-5 grid gap-3">
                <ConsentBox name="affirm_christian" checked={form.affirm_christian} onChange={set("affirm_christian")}>
                  I affirm this business is aligned with the Christian faith.
                </ConsentBox>
                <ConsentBox name="affirm_accurate" checked={form.affirm_accurate} onChange={set("affirm_accurate")}>
                  I affirm the information provided is accurate.
                </ConsentBox>
                <ConsentBox name="affirm_standards" checked={form.affirm_standards} onChange={set("affirm_standards")}>
                  I agree with the directory standards and understand inclusion is not an endorsement.
                </ConsentBox>
              </div>

              <Button type="submit" disabled={submitting}
                className="rounded-full bg-emerald hover:bg-emerald-dark text-white px-10 py-6 text-base" data-testid="business-submit-btn">
                {submitting ? "Submitting…" : "Submit for Review"}
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
